/**
 * Public ArticleList — list published articles. Reads from the generated
 * `cmsArticles` module (populated by `npm run cms:sync` at build time).
 */
import { useTranslation } from 'react-i18next'
import { Container, PageHeader, Reveal } from '@/components/ui'
import ArticleCard from '@/components/cards/ArticleCard'
import SEO from '@/utils/seo'
import { buildBreadcrumbList } from '@/utils/structuredData'
import { cmsArticles } from '@/content/cms/articles.generated'
import { cmsMediaIndex } from '@/content/cms/media.generated'

export default function ArticleList() {
  const { t, i18n } = useTranslation()
  const altLocale = (i18n.language as 'zh-Hant' | 'zh-Hans' | 'en')

  return (
    <>
      <SEO
        title={t('seo.articlesTitle', { defaultValue: '最新文章 — YADA' })}
        description={t('seo.articlesDescription', {
          defaultValue: 'YADA 編輯團隊挑選的教育文章：品格教育趨勢、AI 升學攻略、家校合作實戰。',
        })}
        keywords={t('seo.articlesKeywords', {
          defaultValue:
            'YADA 文章,教育文章,品格教育趨勢,AI 升學攻略,家校合作,生涯規劃,香港教育',
        })}
        jsonLd={[
          // Breadcrumb drives the SERP breadcrumb trail; the list itself
          // is a CollectionPage so crawlers understand it's a paginated
          // index of articles, not a single editorial page.
          buildBreadcrumbList([
            { name: t('nav.home'), path: '/' },
            { name: t('nav.articles'), path: '/articles' },
          ]),
        ]}
      />
      {/* Phase 1 mobile (2026-10-13): the hand-rolled <header> + tagline
          combo was the only content page in the site NOT using the
          shared <PageHeader>. Switched for visual consistency and to
          gain the standard breadcrumb above the title. */}
      <PageHeader
        title={t('nav.articles', { defaultValue: '文章' })}
      />

      <section className="py-section-y px-section-x bg-surface">
        <Container>
          {cmsArticles.length === 0 ? (
            <p className="text-ink-muted">尚無已發佈文章。</p>
          ) : (
            <ul className="grid md:grid-cols-3 gap-gap-cards">
              {cmsArticles.map((a, idx) => (
                <Reveal key={a.slug} delay={idx * 0.1}>
                  <ArticleCard
                    article={a}
                    cover={a.cover_media_uuid ? cmsMediaIndex[a.cover_media_uuid] : undefined}
                    locale={altLocale}
                    variant="compact"
                    readMoreLabel="閱讀全文"
                  />
                </Reveal>
              ))}
            </ul>
          )}
        </Container>
      </section>
    </>
  )
}
